import { makeObservable, observable } from "mobx"

import type { IEventBus } from "@/domains/events"
import { LcAddress } from "@/domains/core"

import type { IGauge, IGaugeRepository, ITopIncentive } from "./interfaces"
import { GaugeAddedToRepositoryEvent } from "./events"
import { GaugeStore } from "./gauge-store"

export class GaugeRepository implements IGaugeRepository {
  gauges = new Map<string, GaugeStore>()

  constructor(private readonly eventBus: IEventBus) {
    makeObservable(this, {
      gauges: observable,
    })
  }

  add(gauge: IGauge): void {
    const existing = this.find(gauge.address)
    if (existing) {
      return
    }

    this.gauges.set(gauge.address.toString(), gauge as GaugeStore)
    this.eventBus.publish(new GaugeAddedToRepositoryEvent(gauge.address))
  }

  find(gaugeAddress: LcAddress): IGauge | undefined {
    return this.gauges.get(gaugeAddress.toString())
  }

  findByRevenueSource(revenueSource: LcAddress): IGauge | undefined {
    return Array.from(this.gauges.values()).find((gauge) => gauge.revenueSourceAddress.equals(revenueSource))
  }

  getAll(): IGauge[] {
    return Array.from(this.gauges.values())
  }

  clearAll(): void {
    this.gauges.clear()
  }

  /**
   * Get top incentives sorted by total incentive value in descending order
   * Only includes gauges with connected revenue sources, valid tokens, and total incentive value > 0
   *
   * This method accesses observables (gauges, revenueSource, totalIncentiveValueUsd, tokens)
   * When called from an observer component, MobX will track all accessed observables
   * and re-run the computation when any of them change.
   */
  getTopIncentivesByGaugeValue(count: number): Array<ITopIncentive> {
    return Array.from(this.gauges.values())
      .filter((gauge) => {
        if (!gauge.revenueSource) return false
        const tokens = gauge.revenueSource.tokens
        if (tokens.length === 0) return false
        return gauge.totalIncentiveValueUsd > 0
      })
      .map((gauge) => {
        const revenueSource = gauge.revenueSource!
        const gaugeApr = Number(gauge.apr)
        return {
          address: revenueSource.address,
          displayType: revenueSource.displayType,
          displayName: revenueSource.displayName,
          tokens: revenueSource.tokens,
          gaugeValueUSD: gauge.totalIncentiveValueUsd,
          apr: revenueSource.apr ?? 0,
          gaugeApr: Number.isNaN(gaugeApr) ? 0 : gaugeApr,
        }
      })
      .sort((a, b) => b.gaugeValueUSD - a.gaugeValueUSD)
      .slice(0, count)
  }
}
